// file: src/pages/landing/Landing.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import TopBar from '../../components/layout/TopBar';
import CreateProjectModal from '../../components/modals/CreateProjectModal';
import ManageServersModal from '../../components/modals/ManageServersModal';
import StorageContentsModal from '../../components/modals/StorageContentsModal';
import { useProjectsData } from '../../hooks/useProjectsData';
import { removeProject } from '../../services/projectServices';
import styles from './Landing.module.css';

// Home page components
import WelcomeSection from './components/WelcomeSection';
import ProjectGrid from './components/ProjectGrid';
import EmptyState from './components/EmptyState';

/**
 * Landing component - Project selection dashboard for authenticated users
 * Displays project selection dashboard with search and quick actions
 */
export default function Landing() {
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showManageServersModal, setShowManageServersModal] = useState(false);
  const [showManageFilesModal, setShowManageFilesModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, project: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const { projects, loading, error, refreshProjects } = useProjectsData();

  // Get current user's tenant ID (you may need to adjust this based on your auth system)
  const getCurrentTenantId = () => {
    // This is a placeholder - you'll need to implement this based on your auth system
    // For now, we'll use a default or get it from localStorage/session
    return localStorage.getItem('currentTenantId') || 'default-tenant';
  };

  // Handle project selection
  const handleProjectClick = (project) => {
    localStorage.setItem("lastProjectId", project.id);
    // Use React Router navigation instead of full page reload to preserve auth state
    navigate(`/projects?project=${project.id}`);
  };

  // Handle file selection from search
  const handleFileSelect = (file, project, phase) => {
    // Store the file ID in localStorage so AppLayout can pick it up
    localStorage.setItem("lastProjectId", project.id);
    localStorage.setItem("selectedFileId", file.id);
    // Navigate to project page - AppLayout will handle opening the file
    navigate(`/projects?project=${project.id}&file=${file.id}`);
  };

  // Handle successful project creation
  const handleProjectCreated = async (projectId) => {
    setShowCreateModal(false);
    
    // Immediately navigate to the new project
    localStorage.setItem("lastProjectId", projectId);
    // Use React Router navigation instead of full page reload to preserve auth state
    navigate(`/projects?project=${projectId}`);
  };

  // Handle project deletion
  const handleDeleteProject = (project, event) => {
    event.stopPropagation(); // Prevent card click
    setDeleteConfirm({ show: true, project });
  };

  const confirmDeleteProject = async () => {
    if (!deleteConfirm.project) return;
    
    setIsDeleting(true);
    try {
      const success = await removeProject(deleteConfirm.project.id);
      if (success) {
        console.log('Project deleted successfully');
        // Refresh the projects list
        await refreshProjects();
        setDeleteConfirm({ show: false, project: null });
      } else {
        console.error('Failed to delete project');
        // Handle error (maybe show a notification)
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      // Handle error
    } finally {
      setIsDeleting(false);
    }
  };

  // Set up CSS variables for animation on component mount
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--animation-color-cycle", "5.5s linear infinite");
    root.style.setProperty(
      "--animation-pulse",
      "5.2s ease-in-out infinite alternate",
    );

    return () => {
      root.style.removeProperty("--animation-color-cycle");
      root.style.removeProperty("--animation-pulse");
    };
  }, []);

  return (
    <div className={styles.landingPage}>
      <TopBar isLanding={true} projects={projects} />
      
      {/* Main content - scrollable */}
      <div className={styles.mainContent}>
        {/* Welcome section with search and quick actions */}
        <div className={styles.welcomeSection}>
          <WelcomeSection
            projects={projects}
            onProjectSelect={handleProjectClick}
            onFileSelect={handleFileSelect}
            onCreateProject={() => setShowCreateModal(true)}
            onManageServers={() => setShowManageServersModal(true)}
            onManageFiles={() => setShowManageFilesModal(true)}
          />
        </div>

        {/* Recent projects section */}
        {projects.length > 0 && (
          <div className={styles.recentProjects}>
            <div className={styles.recentProjectsContainer}>
              <h2 className={styles.recentProjectsTitle}>Recent projects</h2>
              <ProjectGrid
                projects={projects}
                onProjectClick={handleProjectClick}
                onProjectDelete={handleDeleteProject}
                onAddProject={() => setShowCreateModal(true)}
              />
            </div>
          </div>
        )}

        {/* Empty state for no projects */}
        {projects.length === 0 && (
          <div className={styles.emptyState}>
            <EmptyState onCreateProject={() => setShowCreateModal(true)} />
          </div>
        )}
      </div>

        {/* Create project modal */}
        {showCreateModal && (
          <CreateProjectModal
            onClose={() => setShowCreateModal(false)}
            onProjectCreated={handleProjectCreated}
          />
        )}

        {/* Delete confirmation modal */}
        {deleteConfirm.show && (
          <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
              <div className="mb-4">
                <h2 className={styles.modalTitle}>Delete Project</h2>
                <p className={styles.modalDescription}>
                  Are you sure you want to delete "<strong>{deleteConfirm.project?.name}</strong>"? 
                  This action cannot be undone and will permanently delete all project data.
                </p>
              </div>

              <div className={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => setDeleteConfirm({ show: false, project: null })}
                  className={`${styles.modalButton} ${styles.modalButtonSecondary}`}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteProject}
                  className={`${styles.modalButton} ${styles.modalButtonDanger}`}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <div className={styles.loadingSpinner}></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Project
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Manage Servers Modal */}
        {showManageServersModal && (
          <ManageServersModal
            onClose={() => setShowManageServersModal(false)}
            showFilesTab={false}
            title="Manage Development Servers"
          />
        )}

        {/* Manage Files Modal */}
        {showManageFilesModal && (
          <StorageContentsModal
            onClose={() => setShowManageFilesModal(false)}
          />
        )}
    </div>
  );
}
